import type { ReactNode, Element } from 'react';
import React, { Children, useState, useEffect } from 'react';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { t } from 'Src/i18n';
import { Backdrop } from 'Components/frameworks/backdrop/Backdrop';
import { Confirmation } from 'Components/widgets/confirmation/Confirmation';
import { Layer } from 'Components/frameworks/layer/Layer';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
export type ProductInformationLayerProps = {
  activeItem: number;
  backNavigationLabel?: string;
  children: ReactNode;
  errorMessage?: string;
  renderDetail: (data: Record<string, any>) => Node;
  toggleLayer: (arg0: number, arg1: boolean) => void;
};
export const ProductInformationLayer = ({
  activeItem,
  backNavigationLabel,
  children,
  errorMessage,
  renderDetail,
  toggleLayer,
}: ProductInformationLayerProps) => {
  const [state, setState] = useState({
    activeItem,
    isLoading: false,
    isOpen: false,
  });
  useEffect(() => {
    loadDetails();
  }, [activeItem]);

  const closeLayer = () => {
    setState({ ...state, isLoading: false, isOpen: false });
    // -1 to indicate that no product has been selected
    toggleLayer(-1, false);
  };

  const updateDetail = (active: number, detailInfo: Record<string, any>): void => {
    setState({
      activeItem: active,
      detail: detailInfo,
      isLoading: false,
      isOpen: true,
    });
  };

  const getItem = (index: number): Element<any> => {
    return Children.toArray(children)[index];
  };

  const loadDetails = (): void => {
    const item = getItem(activeItem) || {};
    const { props } = item;

    if (props && props.fetchDetail) {
      setState({ ...state, isLoading: true });
      props
        .fetchDetail()
        .then((data) => {
          updateDetail(activeItem, data);
        })
        .catch(() => {
          updateDetail(activeItem, null);
        });
    }
  };

  const renderActiveDetail = () => {
    const item = getItem(activeItem);

    if (!item) {
      return null;
    }

    const leftButton = {
      iconName: ICONS.ARROW_TAIL_LEFT,
      onClick: closeLayer,
      label: backNavigationLabel,
    };
    const ariaLabel = state.detail ? state.detail.label || state.detail.title : '';
    return (
      <Layer
        ariaLabel={ariaLabel}
        bgColor={COLORS.WHITE}
        buttonLeft={leftButton}
        closeLabel={t('common.close')}
        hasAnimateEnter
        hasContainerHeader
        onClose={closeLayer}
      >
        {state.detail ? (
          renderDetail(state.detail)
        ) : (
          <div data-testid="error" className="p-20">
            <div className="mx-auto flex max-w-site justify-center">
              <Confirmation status="error" title={errorMessage} />
            </div>
          </div>
        )}
      </Layer>
    );
  };

  return state.isOpen && !state.isLoading
    ? renderActiveDetail()
    : state.isLoading && (
        <div id="loading" className="relative z-4">
          <Backdrop className="bg-white opacity-90">
            <div
              aria-live="polite"
              className="absolute left-1/2 top-1/2 w-[208px] -translate-x-1/2 -translate-y-1/2 transform text-center"
            >
              <span className="sr-only">Loading</span>
              <Spinner data-testid="Spinner" iconName={ICONS.TRIDENT1} />
            </div>
          </Backdrop>
        </div>
      );
};
